﻿import { useState, useEffect, useRef } from 'react'
import { useConversation } from '@elevenlabs/react'
import { useAuthStore } from '../hooks/useAuthStore'

const ASK_DR_SAM_AGENT_ID = 'agent_8201kjqjf8vqerv88qwhnv02ffye'

/* ── Design tokens (stitch theme) ──────────────────────────── */
const C = {
  primary:            '#4800b2',
  primaryContainer:   '#6200ee',
  onPrimary:          '#ffffff',
  primaryFixed:       '#e8ddff',
  primaryFixedDim:    '#cfbdff',
  onPrimaryFixed:     '#22005d',
  surface:            '#f8f9fd',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow:    '#f2f3f7',
  surfaceContainer:       '#edeef2',
  surfaceContainerHigh:   '#e7e8ec',
  secondaryFixed:     '#e3e0f4',
  tertiaryFixed:      '#ffdbcf',
  onSurface:          '#191c1f',
  onSurfaceVariant:   '#494456',
  outline:            '#7a7488',
  outlineVariant:     '#cbc3d9',
  secondary:          '#5e5d6d',
  tertiary:           '#6f2300',
  error:              '#ba1a1a',
}

export default function AskDrSam() {
  const { user } = useAuthStore()
  const [connectionStatus, setConnectionStatus] = useState('disconnected')
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isUserSpeaking, setIsUserSpeaking] = useState(false)
  const [error, setError] = useState('')
  const [conversationLog, setConversationLog] = useState([])
  const [micPermission, setMicPermission] = useState('unknown')
  const transcriptRef = useRef(null)

  const conversation = useConversation({
    onConnect:    () => { setConnectionStatus('connected'); setError('') },
    onDisconnect: () => { setConnectionStatus('disconnected'); setIsSpeaking(false); setIsUserSpeaking(false) },
    onMessage: (message) => {
      const messageText = message.message || message.text || ''
      const messageRole = message.role || message.type || 'unknown'
      if (messageText) {
        setConversationLog(prev => [...prev, {
          id: `${Date.now()}-${Math.random()}`,
          role: messageRole,
          text: messageText,
          timestamp: new Date()
        }])
      }
    },
    onError: (err) => { setError(err.message || 'An error occurred'); setConnectionStatus('disconnected') },
    onModeChange: (mode) => {
      setIsSpeaking(mode.mode === 'speaking')
      setIsUserSpeaking(mode.mode === 'listening')
    }
  })

  useEffect(() => { checkMicrophonePermission() }, [])

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight
    }
  }, [conversationLog])

  const checkMicrophonePermission = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      stream.getTracks().forEach(t => t.stop())
      setMicPermission('granted')
    } catch {
      setMicPermission('denied')
      setError('Microphone access is required')
    }
  }

  const handleStartConversation = async () => {
    if (micPermission !== 'granted') {
      await checkMicrophonePermission()
      if (micPermission !== 'granted') return
    }
    try {
      setConnectionStatus('connecting'); setError('')
      const apiKey = import.meta.env.VITE_ELEVENLABS_API_KEY
      if (!apiKey) throw new Error('ElevenLabs API key not configured')
      await conversation.startSession({ agentId: ASK_DR_SAM_AGENT_ID })
    } catch (err) {
      setError(err.message || 'Failed to start')
      setConnectionStatus('disconnected')
    }
  }

  const handleEndConversation = async () => { try { await conversation.endSession() } catch {} }
  const handleClearTranscript = () => setConversationLog([])

  const handleDownloadTranscript = () => {
    if (!conversationLog.length) return
    const text = conversationLog.map(l =>
      `[${l.timestamp.toLocaleString()}] ${l.role === 'user' ? 'Patient' : 'Dr Sam'}: ${l.text}`
    ).join('\n\n')
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([text], { type: 'text/plain' })),
      download: `ask-dr-sam-${new Date().toISOString().split('T')[0]}.txt`
    })
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
  }

  const handleCopyTranscript = async () => {
    const text = conversationLog.map(l =>
      `[${l.timestamp.toLocaleString()}] ${l.role === 'user' ? 'Patient' : 'Dr Sam'}: ${l.text}`
    ).join('\n\n')
    try { await navigator.clipboard.writeText(text); alert('Copied!') } catch { alert('Failed to copy') }
  }

  /* ── Derived state ──────────────────────────────────────── */
  const isConnected    = connectionStatus === 'connected'
  const isConnecting   = connectionStatus === 'connecting'
  const isDisconnected = connectionStatus === 'disconnected'

  const orbBorder = isSpeaking
    ? `3px solid ${C.primaryContainer}`
    : isUserSpeaking
    ? '3px solid #22c55e'
    : `2px solid ${C.outlineVariant}`

  const orbBg = isSpeaking || isUserSpeaking
    ? `linear-gradient(135deg, ${C.primaryFixed} 0%, #ddd6fe 100%)`
    : C.surfaceContainerLowest

  const orbShadow = isSpeaking
    ? `0 0 40px rgba(98,0,238,0.18)`
    : 'none'

  const statusLabel =
    isSpeaking      ? 'Dr Sam is speaking…' :
    isUserSpeaking  ? 'Listening to you…'    :
    isConnected     ? 'Connected — speak naturally' :
    isConnecting    ? 'Connecting…'          :
    'Not connected'

  return (
    <div style={{
      maxWidth: 900,
      margin: '0 auto',
      fontFamily: "'Inter','Segoe UI',sans-serif",
      color: C.onSurface,
    }}>

      {/* ── Page heading ──────────────────────────────────── */}
      <div style={{ textAlign: 'center', marginBottom: 36 }}>
        <h2 style={{
          fontSize: 26, fontWeight: 800,
          fontFamily: "'Manrope','Inter',sans-serif",
          color: C.onSurface, marginBottom: 12, letterSpacing: '-0.5px',
        }}>
          Ask Dr Sam — AI Medical Assistant
        </h2>

        {/* Animated status badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 16px',
          background: C.secondaryFixed,
          borderRadius: 9999,
          fontSize: 13, fontWeight: 500,
          color: C.onSurfaceVariant,
        }}>
          <span style={{ position: 'relative', display: 'flex', width: 8, height: 8 }}>
            {isConnected && (
              <span style={{
                position: 'absolute', width: '100%', height: '100%',
                borderRadius: '50%', background: C.primaryContainer,
                animation: 'ping 1.2s cubic-bezier(0,0,0.2,1) infinite',
                opacity: 0.75,
              }} />
            )}
            <span style={{
              display: 'inline-flex', width: 8, height: 8,
              borderRadius: '50%',
              background: isConnected ? C.primaryContainer : isConnecting ? '#f59e0b' : C.outlineVariant,
            }} />
          </span>
          {isConnected ? 'Live — Active Session' : isConnecting ? 'Connecting to Dr Sam…' : 'Ready to Talk with Dr Sam'}
        </div>
      </div>

      {/* ── Central AI orb area ───────────────────────────── */}
      <div style={{
        background: `linear-gradient(135deg, rgba(72,0,178,0.04) 0%, rgba(98,0,238,0.08) 100%)`,
        borderRadius: 24,
        border: `1px solid rgba(98,0,238,0.12)`,
        boxShadow: '0 0 40px rgba(72,0,178,0.07)',
        padding: '48px 24px',
        marginBottom: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}>
        {/* Orb */}
        <div style={{ position: 'relative', marginBottom: 20 }}>
          <div style={{
            width: 128, height: 128,
            background: orbBg,
            borderRadius: '50%',
            border: orbBorder,
            boxShadow: orbShadow,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.4s ease',
          }}>
            {isSpeaking ? (
              <span style={{ fontSize: 56 }}>🔊</span>
            ) : isUserSpeaking ? (
              <i className="uil uil-microphone" style={{ fontSize: 52, color: '#22c55e' }} />
            ) : (
              <i className="uil uil-stethoscope" style={{ fontSize: 52, color: C.primaryContainer }} />
            )}
          </div>

          {/* Mic status badge */}
          <div style={{
            position: 'absolute', bottom: -4, right: -4,
            background: C.surfaceContainerLowest,
            borderRadius: '50%', padding: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}>
            <i
              className={isConnected ? 'uil uil-microphone' : 'uil uil-microphone-slash'}
              style={{ fontSize: 16, color: isConnected ? C.primaryContainer : C.outline }}
            />
          </div>
        </div>

        {/* Status text */}
        <h3 style={{
          fontSize: 22, fontWeight: 700,
          fontFamily: "'Manrope','Inter',sans-serif",
          color: C.onSurface, marginBottom: 8,
        }}>{statusLabel}</h3>

        {isDisconnected && (
          <p style={{ color: C.onSurfaceVariant, fontSize: 14, maxWidth: 380, marginBottom: 24, lineHeight: 1.6 }}>
            Click the button below to start your clinical consultation with our medical AI assistant.
          </p>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: '#fef2f2', border: `1px solid #fecaca`,
            borderRadius: 12, padding: '10px 18px',
            display: 'flex', alignItems: 'center', gap: 8,
            color: C.error, fontSize: 13, marginBottom: 16,
          }}>
            <i className="uil uil-exclamation-circle" style={{ fontSize: 16 }} />
            {error}
            {micPermission === 'denied' && (
              <button onClick={checkMicrophonePermission} style={{
                marginLeft: 8, padding: '2px 10px', fontSize: 12,
                border: `1px solid ${C.error}`, borderRadius: 8,
                background: 'none', color: C.error, cursor: 'pointer',
              }}>Retry</button>
            )}
          </div>
        )}

        {/* Action button */}
        {isDisconnected ? (
          <button
            onClick={handleStartConversation}
            disabled={micPermission === 'denied'}
            style={{
              padding: '14px 32px',
              background: micPermission === 'denied' ? C.outlineVariant : C.primary,
              color: C.onPrimary,
              borderRadius: 9999,
              border: 'none',
              fontFamily: "'Manrope','Inter',sans-serif",
              fontWeight: 700, fontSize: 15,
              display: 'inline-flex', alignItems: 'center', gap: 10,
              cursor: micPermission === 'denied' ? 'not-allowed' : 'pointer',
              transition: 'all 0.15s',
              boxShadow: micPermission === 'denied' ? 'none' : '0 4px 16px rgba(72,0,178,0.3)',
            }}
          >
            <i className="uil uil-microphone" style={{ fontSize: 20 }} />
            Connect to Dr Sam
          </button>
        ) : isConnecting ? (
          <button disabled style={{
            padding: '14px 32px',
            background: C.primary, color: C.onPrimary,
            borderRadius: 9999, border: 'none',
            fontFamily: "'Manrope','Inter',sans-serif",
            fontWeight: 700, fontSize: 15,
            display: 'inline-flex', alignItems: 'center', gap: 10,
            opacity: 0.8, cursor: 'default',
          }}>
            <span style={{
              width: 16, height: 16, border: '2px solid #ffffff44',
              borderTop: '2px solid #fff', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite', display: 'inline-block',
            }} />
            Connecting…
          </button>
        ) : (
          <button
            onClick={handleEndConversation}
            style={{
              padding: '14px 32px',
              background: '#ba1a1a', color: '#fff',
              borderRadius: 9999, border: 'none',
              fontFamily: "'Manrope','Inter',sans-serif",
              fontWeight: 700, fontSize: 15,
              display: 'inline-flex', alignItems: 'center', gap: 10,
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(186,26,26,0.3)',
            }}
          >
            <i className="uil uil-phone-times" style={{ fontSize: 20 }} />
            End Conversation
          </button>
        )}

        {/* Mic ring when connected */}
        {isConnected && (
          <div style={{
            marginTop: 24,
            width: 72, height: 72,
            borderRadius: '50%',
            border: `3px solid ${isUserSpeaking ? '#22c55e' : C.outlineVariant}`,
            background: isUserSpeaking ? '#dcfce7' : C.surfaceContainerLow,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.3s',
          }}>
            <i className={`uil uil-microphone`}
              style={{ fontSize: 28, color: isUserSpeaking ? '#16a34a' : C.outline }} />
          </div>
        )}
      </div>

      {/* ── Live Transcript ───────────────────────────────── */}
      <div style={{
        background: C.surfaceContainerLow,
        borderRadius: 16,
        border: `1px solid rgba(203,195,217,0.2)`,
        marginBottom: 24,
        overflow: 'hidden',
      }}>
        {/* Transcript header */}
        <div style={{
          padding: '14px 20px',
          borderBottom: `1px solid ${C.outlineVariant}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: C.surfaceContainerLowest,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="uil uil-receipt-alt" style={{ fontSize: 18, color: C.primaryContainer }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: C.onSurface, fontFamily: "'Manrope',sans-serif" }}>
              Live Conversation Transcript
            </span>
            {conversationLog.length > 0 && (
              <span style={{
                background: C.primaryContainer, color: '#fff',
                borderRadius: 9999, fontSize: 10, fontWeight: 700,
                padding: '2px 7px',
              }}>{conversationLog.length}</span>
            )}
          </div>
          {conversationLog.length > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { label: 'Copy',  icon: 'uil-clipboard',    fn: handleCopyTranscript,     bg: C.primaryContainer },
                { label: 'Save',  icon: 'uil-download-alt', fn: handleDownloadTranscript, bg: '#16a34a' },
                { label: 'Clear', icon: 'uil-trash-alt',    fn: handleClearTranscript,    bg: '#ba1a1a' },
              ].map(b => (
                <button key={b.label} onClick={b.fn} style={{
                  padding: '5px 12px', fontSize: 12, fontWeight: 600,
                  background: b.bg, color: '#fff',
                  border: 'none', borderRadius: 8, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                  <i className={`uil ${b.icon}`} style={{ fontSize: 13 }} />
                  {b.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Transcript body */}
        <div
          ref={transcriptRef}
          style={{
            minHeight: 160, maxHeight: 320, overflowY: 'auto',
            padding: '20px',
          }}
        >
          {conversationLog.length > 0 ? (
            conversationLog.map(log => {
              const isUser = log.role === 'user' || log.role === 'user_message'
              return (
                <div key={log.id} style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  marginBottom: 16,
                }}>
                  <div style={{ maxWidth: '80%' }}>
                    <div style={{
                      fontSize: 11, fontWeight: 600,
                      color: isUser ? C.primaryContainer : '#16a34a',
                      marginBottom: 4,
                      textAlign: isUser ? 'right' : 'left',
                    }}>
                      {isUser ? '👤 You' : '🩺 Dr Sam'} · {log.timestamp.toLocaleTimeString()}
                    </div>
                    <div style={{
                      padding: '10px 14px',
                      borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                      background: isUser ? C.primaryContainer : C.surfaceContainerLowest,
                      color: isUser ? '#fff' : C.onSurface,
                      border: isUser ? 'none' : `1px solid ${C.outlineVariant}`,
                      fontSize: 13, lineHeight: 1.6,
                    }}>
                      {log.text}
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div style={{
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              minHeight: 120, color: C.outline, textAlign: 'center',
            }}>
              <i className="uil uil-receipt-alt" style={{ fontSize: 32, marginBottom: 8 }} />
              <p style={{ fontSize: 13, margin: 0, fontWeight: 500 }}>
                Start a conversation to see the transcript here
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── Bento feature cards ───────────────────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 16,
        marginBottom: 24,
      }}>
        {[
          {
            icon: 'uil-volume',
            iconBg: C.primaryFixed, iconColor: C.primary,
            title: 'Voice First',
            desc: 'Natural language processing optimized for clinical terminology and patient symptoms.',
          },
          {
            icon: 'uil-bolt',
            iconBg: C.secondaryFixed, iconColor: C.secondary,
            title: 'Real-time',
            desc: 'Instant medical knowledge retrieval with ultra-low latency for seamless consultation.',
          },
          {
            icon: 'uil-shield-check',
            iconBg: C.tertiaryFixed, iconColor: C.tertiary,
            title: 'Secure',
            desc: 'End-to-end encrypted consultations compliant with HIPAA and GDPR standards.',
          },
        ].map((card, i) => (
          <div key={i} style={{
            background: C.surfaceContainerLowest,
            borderRadius: 16,
            border: `1px solid ${C.outlineVariant}33`,
            padding: '24px 20px',
            transition: 'border-color 0.2s',
          }}>
            <div style={{
              width: 40, height: 40,
              background: card.iconBg,
              borderRadius: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 14,
            }}>
              <i className={`uil ${card.icon}`} style={{ fontSize: 20, color: card.iconColor }} />
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.onSurface, fontFamily: "'Manrope',sans-serif", marginBottom: 6 }}>
              {card.title}
            </div>
            <div style={{ fontSize: 12, color: C.onSurfaceVariant, lineHeight: 1.6 }}>
              {card.desc}
            </div>
          </div>
        ))}
      </div>

      {/* ── Disclaimer ────────────────────────────────────── */}
      <div style={{
        background: C.surfaceContainerHigh + '80',
        borderRadius: 12,
        padding: '14px 18px',
        display: 'flex', alignItems: 'flex-start', gap: 12,
        marginBottom: 8,
      }}>
        <i className="uil uil-info-circle" style={{ fontSize: 18, color: C.error, flexShrink: 0, marginTop: 1 }} />
        <p style={{ fontSize: 11, color: C.onSurfaceVariant, margin: 0, lineHeight: 1.7 }}>
          <strong style={{ color: C.onSurface }}>Medical Disclaimer: </strong>
          Ask Dr Sam is an AI medical assistant designed for information and clinical intelligence support.
          It does not provide medical diagnoses or replace professional medical advice.
          Always consult with a qualified healthcare provider for personal health concerns.
          In case of emergency, contact your local emergency services immediately.
        </p>
      </div>

      {/* ── CSS keyframes ─────────────────────────────────── */}
      <style>{`
        @keyframes ping {
          75%, 100% { transform: scale(2); opacity: 0; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
